import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { 
  Typography, 
  Box, 
  CircularProgress, 
  Alert, 
  Tooltip, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  InputAdornment
} from "@mui/material";
import { API_ENDPOINTS } from "../config/api.config";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

export default function Users() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Stato per la ricerca
  const [searchQuery, setSearchQuery] = useState('');

  // Stato per il dialog di create/update
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    username: '',
    email: '',
    role_id: '',
    org_role_id: '',
    id_people_manager: '',
  });
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Stato per i ruoli progetto
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  
  // Stato per i ruoli gerarchici (people_roles)
  const [peopleRoles, setPeopleRoles] = useState([]);
  const [peopleRolesLoading, setPeopleRolesLoading] = useState(false);
  
  // Stato per i people managers (utenti disponibili)
  const [peopleManagers, setPeopleManagers] = useState([]);
  const [peopleManagersLoading, setPeopleManagersLoading] = useState(false);

  // Fetch people managers (users available to be people managers)
  const fetchPeopleManagers = async () => {
    setPeopleManagersLoading(true);
    try {
      console.log('[PEOPLE_MANAGERS] Fetching from:', API_ENDPOINTS.USERS);
      const response = await fetch(API_ENDPOINTS.USERS);
      console.log('[PEOPLE_MANAGERS] Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.log('[PEOPLE_MANAGERS] Error response:', errorText);
        throw new Error(`API Error ${response.status}: ${errorText || 'Unknown error'}`);
      }
      
      const data = await response.json();
      console.log('[PEOPLE_MANAGERS] Response data:', data);
      const usersList = data.users || data || [];
      console.log('[PEOPLE_MANAGERS] Users loaded:', usersList.length, 'items');
      setPeopleManagers(usersList);
    } catch (err) {
      console.error('[PEOPLE_MANAGERS] Fetch error:', err);
      // Don't set main error for people managers, just log it
    } finally {
      setPeopleManagersLoading(false);
    }
  };

  // Fetch roles (project roles)
  const fetchRoles = async () => {
    setRolesLoading(true);
    try {
      console.log('[ROLES] Fetching from:', API_ENDPOINTS.ROLES);
      const response = await fetch(API_ENDPOINTS.ROLES);
      console.log('[ROLES] Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.log('[ROLES] Error response:', errorText);
        throw new Error(`API Error ${response.status}: ${errorText || 'Unknown error'}`);
      }
      
      const data = await response.json();
      console.log('[ROLES] Response data:', data);
      const rolesList = data.roles || data || [];
      console.log('[ROLES] Roles loaded:', rolesList.length, 'items');
      setRoles(rolesList);
    } catch (err) {
      console.error('[ROLES] Fetch error:', err);
      // Don't set main error for roles, just log it
    } finally {
      setRolesLoading(false);
    }
  };

  // Fetch people roles (hierarchical roles)
  const fetchPeopleRoles = async () => {
    setPeopleRolesLoading(true);
    try {
      console.log('[PEOPLE_ROLES] Fetching from:', API_ENDPOINTS.PEOPLE_ROLES);
      const response = await fetch(API_ENDPOINTS.PEOPLE_ROLES);
      console.log('[PEOPLE_ROLES] Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.log('[PEOPLE_ROLES] Error response:', errorText);
        throw new Error(`API Error ${response.status}: ${errorText || 'Unknown error'}`);
      }
      
      const data = await response.json();
      console.log('[PEOPLE_ROLES] Response data:', data);
      const peopleRolesList = data.people_roles || data || [];
      console.log('[PEOPLE_ROLES] People roles loaded:', peopleRolesList.length, 'items');
      setPeopleRoles(peopleRolesList);
    } catch (err) {
      console.error('[PEOPLE_ROLES] Fetch error:', err);
      // Don't set main error for people roles, just log it
    } finally {
      setPeopleRolesLoading(false);
    }
  };

  // Fetch users
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('[USERS] Fetching from:', API_ENDPOINTS.USERS);
      const response = await fetch(API_ENDPOINTS.USERS);
      console.log('[USERS] Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.log('[USERS] Error response:', errorText);
        throw new Error(`API Error ${response.status}: ${errorText || 'Unknown error'}`);
      }
      
      const data = await response.json();
      console.log('[USERS] Response data:', data);
      const users = data.users || data || [];
      console.log('[USERS] Users loaded:', users.length, 'items');
      
      // Transform the data to match the expected structure for DataGrid
      const transformedUsers = users.map((row, idx) => {
        const roleName =
          (row.role && typeof row.role === 'object' && row.role.name) ||
          row.role_name ||
          (typeof row.role === 'string' ? row.role : '') ||
          'Unknown';
        let roleCode =
          (row.role && typeof row.role === 'object' && row.role.code) ||
          row.role_code ||
          '';
        // Se non c'è un codice, mappa dal nome del ruolo
        if (!roleCode && roleName) {
          const normalizedName = roleName.toLowerCase().trim();
          const codeMapping = {
            'service_manager': 'S',
            'jr_service_manager': 'J',
            'people_manager': 'P',
            'team_leader': 'T',
            'team_member': 'C',
            'admin': 'A'
          };
          roleCode = codeMapping[normalizedName] || '';
        }
        const roleDescription =
          (row.role && typeof row.role === 'object' && row.role.description) ||
          row.role_description ||
          '';
        const roleId =
          (row.role && typeof row.role === 'object' && row.role.id) ||
          row.role_id ||
          null;

        const roleData =
          row.role && typeof row.role === 'object'
            ? {
                ...row.role,
                id: roleId,
                name: row.role.name || roleName,
                code: row.role.code || roleCode,
                description: row.role.description || roleDescription,
              }
            : {
                id: roleId,
                name: roleName,
                code: roleCode,
                description: roleDescription,
              };

        return {
          id: row.id || idx,
          username: row.username || '',
          email: row.email || '',
          role: roleData,
          role_id: roleId,
          role_name: roleName,
          role_code: roleCode,
          role_description: roleDescription,
          org_role_id: row.org_role_id,
          org_role: row.org_role, // Include org_role object for editing
          level_id: row.level_id,
          company_id: row.company_id,
          id_people_manager: row.id_people_manager,
          people_manager: row.people_manager,
        };
      });
      
      setRows(transformedUsers);
    } catch (err) {
      console.error('[USERS] Fetch error:', err);
      setError(`Error loading users: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchPeopleRoles();
    fetchUsers();
    fetchPeopleManagers();
  }, []);

  // Mappa i nomi dei ruoli ai codici
  const getRoleCodeFromName = (roleName) => {
    if (!roleName) return '';
    const normalizedName = roleName.toLowerCase().trim();
    const mapping = {
      'service_manager': 'S',
      'jr_service_manager': 'J',
      'people_manager': 'P',
      'team_leader': 'T',
      'team_member': 'C',
      'admin': 'A'
    };
    return mapping[normalizedName] || '';
  };

  const getRoleColor = (roleCode) => {
    const colors = {
      'S': '#1976d2', // Service Manager - Blue
      'J': '#388e3c', // JR Service Manager - Green
      'P': '#f57c00', // People Manager - Orange
      'T': '#7b1fa2', // Team Leader - Purple
      'C': '#757575'  // Team Member - Gray
    };
    return colors[roleCode] || '#757575';
  };

  // Filtra gli utenti in base alla query di ricerca
  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) {
      return rows;
    }
    
    const query = searchQuery.toLowerCase().trim();
    
    return rows.filter(row => {
      // Cerca in username
      if (row.username && row.username.toLowerCase().includes(query)) {
        return true;
      }
      
      // Cerca in email
      if (row.email && row.email.toLowerCase().includes(query)) {
        return true;
      }
      
      // Cerca in project role name
      if (row.role_name && row.role_name.toLowerCase().includes(query)) {
        return true;
      }
      
      // Cerca in org role name
      if (row.org_role?.name && row.org_role.name.toLowerCase().includes(query)) {
        return true;
      }
      
      // Cerca in people manager username
      if (row.people_manager?.username && row.people_manager.username.toLowerCase().includes(query)) {
        return true;
      }
      
      // Cerca in people manager email
      if (row.people_manager?.email && row.people_manager.email.toLowerCase().includes(query)) {
        return true;
      }
      
      // Cerca per ID
      if (row.id && row.id.toString().includes(query)) {
        return true;
      }
      
      return false;
    });
  }, [rows, searchQuery]);

  // Colonne della tabella
  const columns = [
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton onClick={() => navigate(`/users/${params.row.id}`)} size="small">
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton onClick={() => handleEditDialog(params.row)} size="small">
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete user (currently disabled)">
            <IconButton 
              onClick={() => handleDelete(params.row.id)} 
              size="small"
              disabled
              sx={{ opacity: 0.3 }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    },
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'username', headerName: 'Username', width: 250, flex: 1 },
    { field: 'email', headerName: 'Email', width: 350, flex: 1 },
    { 
      field: 'role', 
      headerName: 'Project Role', 
      width: 150,
      valueGetter: (value, row) => {
        // Estrae il nome del ruolo come stringa per il filtro
        if (row.role && typeof row.role === 'object' && row.role.name) {
          return row.role.name;
        }
        if (row.role_name) {
          return row.role_name;
        }
        if (typeof row.role === 'string') {
          return row.role;
        }
        return typeof value === 'string' ? value : 'Unknown';
      },
      renderCell: (params) => {
        const roleObj = params.row.role && typeof params.row.role === 'object' ? params.row.role : null;
        const roleName =
          (roleObj && roleObj.name) ||
          params.row.role_name ||
          (typeof params.row.role === 'string' ? params.row.role : params.value) ||
          'Unknown';
        // Estrai il codice: prima prova dal codice diretto, poi dal nome del ruolo
        let roleCode = (roleObj && roleObj.code) || params.row.role_code || '';
        // Se non c'è un codice, prova a mappare dal nome del ruolo
        if (!roleCode && roleName) {
          roleCode = getRoleCodeFromName(roleName);
        }
        const roleDescription = (roleObj && roleObj.description) || params.row.role_description || '';

        const chip = (
          <Chip 
            label={roleName} 
            size="small" 
            sx={{
              backgroundColor: getRoleColor(roleCode),
              color: 'white'
            }}
          />
        );

        if (roleDescription) {
          return (
            <Tooltip title={roleDescription} placement="top" arrow>
              <Box component="span">{chip}</Box>
            </Tooltip>
          );
        }

        return chip;
      }
    },
    { 
      field: 'org_role', 
      headerName: 'Hierarchical Role', 
      width: 180,
      valueGetter: (value, row) => {
        // Estrae il nome del ruolo gerarchico come stringa per il filtro
        if (row.org_role?.name) {
          return row.org_role.name;
        }
        return 'Not assigned';
      },
      renderCell: (params) => {
        const orgRole = params.row.org_role;
        const orgRoleName = orgRole?.name;

        const inner = !orgRoleName ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            Not assigned
          </Typography>
        ) : (
          <Chip label={orgRoleName} size="small" variant="outlined" color="primary" />
        );

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            {inner}
          </Box>
        );
      }
    },
    { 
      field: 'people_manager', 
      headerName: 'People Manager', 
      width: 200,
      valueGetter: (value, row) => {
        // Estrae username e email come stringa per il filtro
        const peopleManager = row.people_manager;
        if (!peopleManager) {
          return 'Not assigned';
        }
        return `${peopleManager.username || ''} ${peopleManager.email || ''}`.trim();
      },
      renderCell: (params) => {
        const peopleManager = params.row.people_manager;
        if (!peopleManager) {
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                width: '100%',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Not assigned
              </Typography>
            </Box>
          );
        }
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, py: 0.25 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, lineHeight: 1.2, m: 0 }}
              >
                {peopleManager.username}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ lineHeight: 1.2, m: 0, display: 'block' }}
              >
                {peopleManager.email}
              </Typography>
            </Box>
          </Box>
        );
      }
    },
  ];

  // Gestione dialog
  const handleOpenDialog = () => {
    setFormData({
      id: '',
      username: '',
      email: '',
      role_id: '',
      org_role_id: '',
      id_people_manager: '',
    });
    setEditingRow(null);
    setFormError(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormError(null);
  };

  const handleEditDialog = (row) => {
    setFormData({
      id: row.id || '',
      username: row.username || '',
      email: row.email || '',
      role_id: row.role_id || row.role?.id || '',
      org_role_id: row.org_role_id || row.org_role?.id || '',
      id_people_manager: row.id_people_manager || row.people_manager?.id || '',
    });
    setEditingRow(row);
    setFormError(null);
    setOpenDialog(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
    
    // Reset form error quando l'utente modifica i campi
    if (formError) {
      setFormError(null);
    }
  };



  // Funzione per validare email in tempo reale
  const validateEmail = (email) => {
    if (!email.trim()) return null; // Non mostrare errore se vuoto (gestito dalla validazione required)
    
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    const cleanEmail = email.trim().toLowerCase();
    
    if (!emailRegex.test(cleanEmail)) {
      return 'Invalid email format';
    }
    
    if (cleanEmail.length > 254) {
      return 'Email too long (max 254 characters)';
    }
    
    if (cleanEmail.includes('..')) {
      return 'Cannot contain consecutive dots';
    }
    
    if (cleanEmail.startsWith('.') || cleanEmail.endsWith('.')) {
      return 'Cannot start or end with a dot';
    }
    
    return null;
  };

  // Salva user (create/update)
  const handleSaveUser = async () => {
    setSaving(true);
    setFormError(null);
    
    if (!formData.username.trim()) {
      setFormError('Username is required.');
      setSaving(false);
      return;
    }

    if (!formData.email.trim()) {
      setFormError('Email is required.');
      setSaving(false);
      return;
    }

    if (!formData.role_id) {
      setFormError('Project Role is required.');
      setSaving(false);
      return;
    }

    if (!formData.org_role_id) {
      setFormError('Hierarchical Role is required.');
      setSaving(false);
      return;
    }

    if (!formData.id_people_manager) {
      setFormError('People Manager is required.');
      setSaving(false);
      return;
    }

    // Validazione email più rigorosa
    const email = formData.email.trim().toLowerCase();
    
    // Regex più completa per email validation
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    
    if (!emailRegex.test(email)) {
      setFormError('Please enter a valid email address.');
      setSaving(false);
      return;
    }

    // Controlli aggiuntivi per email
    if (email.length > 254) {
      setFormError('Email address is too long (max 254 characters).');
      setSaving(false);
      return;
    }

    if (email.includes('..')) {
      setFormError('Email address cannot contain consecutive dots.');
      setSaving(false);
      return;
    }

    if (email.startsWith('.') || email.endsWith('.')) {
      setFormError('Email address cannot start or end with a dot.');
      setSaving(false);
      return;
    }

    // Controlla email duplicate (solo per nuovi utenti)
    if (!editingRow) {
      const existingUser = rows.find(user => 
        user.email && user.email.toLowerCase() === email
      );
      if (existingUser) {
        setFormError('A user with this email address already exists.');
        setSaving(false);
        return;
      }
    } else {
      // Per update, controlla che l'email non sia già usata da un altro utente
      const existingUser = rows.find(user => 
        user.email && 
        user.email.toLowerCase() === email && 
        user.id !== editingRow.id
      );
      if (existingUser) {
        setFormError('Another user is already using this email address.');
        setSaving(false);
        return;
      }
    }

    try {
      let payload, url, method;
      
      if (editingRow) {
        // Update - usa PUT come specificato nell'API
        payload = {
          username: formData.username.trim(),
          email: email, // Usa email normalizzata (trim + toLowerCase)
          role_id: formData.role_id,
          org_role_id: formData.org_role_id,
          id_people_manager: formData.id_people_manager,
        };
        url = `${API_ENDPOINTS.USERS}?id=${editingRow.id}`;
        method = 'PUT';
      } else {
        // Create - non includere l'ID
        payload = {
          username: formData.username.trim(),
          email: email, // Usa email normalizzata (trim + toLowerCase)
          role_id: formData.role_id,
          org_role_id: formData.org_role_id,
          id_people_manager: formData.id_people_manager,
        };
        url = API_ENDPOINTS.USERS;
        method = 'POST';
      }

      console.log(`[USERS] ${method} request to:`, url);
      console.log('[USERS] Payload:', payload);

      const response = await fetch(url, {
        method: method,
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      console.log('[USERS] Response status:', response.status);
      
      if (!response.ok) {
        const err = await response.text();
        console.log('[USERS] Error response:', err);
        throw new Error(`${response.status}: ${err || 'Request failed'}`);
      }

      const result = await response.json();
      console.log('[USERS] Save successful:', result);

      await fetchUsers();
      setOpenDialog(false);
    } catch (error) {
      console.error('[USERS] Save error:', error);
      setFormError(`Error saving user: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Elimina user (currently disabled)
  const handleDelete = async (id) => {
    // Feature temporarily disabled
    alert('User deletion is currently disabled.');
    return;
    
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      console.log('[USERS] Deleting user:', id);
      const response = await fetch(`${API_ENDPOINTS.USERS}?id=${id}`, {
        method: 'DELETE'
      });

      console.log('[USERS] Delete response status:', response.status);
      
      if (!response.ok) {
        const err = await response.text();
        console.log('[USERS] Delete error response:', err);
        throw new Error(`${response.status}: ${err || 'Delete failed'}`);
      }

      console.log('[USERS] Delete successful');
      await fetchUsers();
    } catch (error) {
      console.error('[USERS] Delete error:', error);
      alert(`Error deleting user: ${error.message}`);
    }
  };

  return (
    <Box>
      <Box my={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" gutterBottom sx={{ fontSize: '1.5rem' }}>
            Users Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            color="primary"
            onClick={handleOpenDialog}
          >
            Add User
          </Button>
        </Box>
        
        {/* Barra di ricerca */}
        <Box mb={2}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by username, email, role, people manager, or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    edge="end"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ 
              bgcolor: 'white',
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
              }
            }}
          />
          {searchQuery && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Found {filteredRows.length} user{filteredRows.length !== 1 ? 's' : ''} matching "{searchQuery}"
            </Typography>
          )}
        </Box>
        
        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 100, page: 0 },
                },
              }}
              pageSizeOptions={[5, 10, 25, 50, 100]}
              pagination
              sx={{ bgcolor: 'white' }}
              disableRowSelectionOnClick
            />
          </Box>
        )}
      </Box>

      {/* Dialog per create/update user */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRow ? 'Edit User' : 'New User'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              name="username"
              label="Username"
              value={formData.username}
              onChange={handleFormChange}
              required
              fullWidth
              error={!formData.username.trim() && formError}
              helperText={!formData.username.trim() && formError ? 'Username is required' : ''}
            />
            
            <TextField
              name="email"
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleFormChange}
              required
              fullWidth
              error={(!formData.email.trim() && formError) || validateEmail(formData.email) !== null}
              helperText={
                !formData.email.trim() && formError 
                  ? 'Email is required' 
                  : validateEmail(formData.email) || ''
              }
            />
            
            <FormControl fullWidth required>
              <InputLabel>Project Role</InputLabel>
              <Select
                name="role_id"
                value={formData.role_id}
                onChange={handleFormChange}
                label="Project Role"
                disabled={rolesLoading}
                required
              >
                {rolesLoading ? (
                  <MenuItem disabled>Loading roles...</MenuItem>
                ) : roles.length === 0 ? (
                  <MenuItem disabled>No roles available</MenuItem>
                ) : (
                  roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name} - {role.description}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            
            <FormControl fullWidth required>
              <InputLabel>Hierarchical Role</InputLabel>
              <Select
                name="org_role_id"
                value={formData.org_role_id}
                onChange={handleFormChange}
                label="Hierarchical Role"
                disabled={peopleRolesLoading}
                required
              >
                {peopleRolesLoading ? (
                  <MenuItem disabled>Loading hierarchical roles...</MenuItem>
                ) : peopleRoles.length === 0 ? (
                  <MenuItem disabled>No hierarchical roles available</MenuItem>
                ) : (
                  peopleRoles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name} - {role.description}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            
            <FormControl fullWidth required>
              <InputLabel>People Manager</InputLabel>
              <Select
                name="id_people_manager"
                value={formData.id_people_manager}
                onChange={handleFormChange}
                label="People Manager"
                disabled={peopleManagersLoading}
                required
              >
                {peopleManagersLoading ? (
                  <MenuItem disabled>Loading users...</MenuItem>
                ) : peopleManagers.length === 0 ? (
                  <MenuItem disabled>No users available</MenuItem>
                ) : (
                  peopleManagers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.username} - {user.email}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            
            {formError && (
              <Alert severity="error">{formError}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveUser} 
            variant="contained"
            color="primary" 
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 